import { enableProdMode, Injector, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';

import { createTranslateLoader } from './app/app.module';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { PDFViewerModule } from '@progress/kendo-angular-pdfviewer';
import { CommonModule } from '@angular/common';
import { TextMissingTranslationHandler } from '../projects/qpoint-i18n/src/lib/services/text-missing-translation.handler';
import { TranslateModule, TranslateLoader, MissingTranslationHandler } from '@ngx-translate/core';
import { SortableModule } from '@progress/kendo-angular-sortable';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { FloatingLabelModule } from '@progress/kendo-angular-label';
import { IntlModule } from '@progress/kendo-angular-intl';
import { NgbModule, NgbAlertModule } from '@ng-bootstrap/ng-bootstrap';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { AppRoutingModule } from './app/routes/app-routing.module';
import { UploadModule } from '@progress/kendo-angular-upload';
import { PDFModule, ExcelModule } from '@progress/kendo-angular-grid';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MarkdownModule } from 'ngx-markdown';
import { provideAnimations } from '@angular/platform-browser/animations';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { GoogleMapsModule } from '@angular/google-maps';
import { withInterceptorsFromDi, provideHttpClient, HttpClient, withJsonpSupport } from '@angular/common/http';
import { KendoIconSettingsService } from '../projects/qpoint-forms/src/lib/services/kendo-icon-settings.service';
import { IconSettingsService } from '@progress/kendo-angular-icons';
import { DI_LOCALIZATION_INFO } from '../projects/qpoint-i18n/src/public_api';
import { TimePickerConfigService } from './app/services/time-picker-config.service';
import { DI_TIME_PICKER_FORMAT_PLACEHOLDER } from '../projects/qpoint-forms/src/public_api';
import { fal } from '@fortawesome/pro-light-svg-icons';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { faDatabase, far } from '@fortawesome/pro-regular-svg-icons';
import { DI_FONTAWESOME_ICONS, DI_FONTAWESOME_ICON_PACKS, QPointIconModule } from '../projects/qpoint-icons/src/lib/q-point-icon.module';
import { MapsApiKeyService } from './app/services/maps-api-key.service';
import { DI_MAPS_API_KEY_SERVICE } from '../projects/qpoint-maps/src/lib/services/google-api-key.service';
import { provideImageLoaderService, QPointGridModule } from '@qpoint/layout';
import { NumberFormatService } from './app/services/number-format.service';
import { UserInfoSidebarProviderService } from './app/services/user-info-sidebar-provider.service';
import { provideUserInfoSidebar, SidebarSizes, QPointContextbarService } from '../projects/qpoint-layout/src/public_api';
import { ImageLoaderService } from './app/services/image-loader.service';
import { DI_IMAGE_LOADER_SERVICE } from '../projects/qpoint-layout/src/lib/services/image-loader.service';
import { QPointProfileService } from './app/services/qpoint-profile.service';
import { DI_USER_PROFILE_SERVICE, provideUserProfileService } from '../projects/qpoint-profile/src/public_api';
import { Settings } from './app/common';
import { Authentication } from './app/auth';
import { provideAuthentication, DI_SETTINGS } from '@qpoint/utils';
import { MENU } from './app/app.menu';
import { QPointSidebarService } from '../projects/qpoint-layout/src/lib/components';
import { LocalizationNamespaceService } from './app/services/localization-namespace.service';
import { LocalizationInfoService } from './app/services/localization-info.service';
import { QPointLocalizationService, provideLocalizationInfo, provideNumberFormatService, provideQPointI18n, DI_UNIT_TRANSLATIONS_BASE_PATH } from '@qpoint/i18n';

const FONTAWESOME_ICONS = [
  faDatabase
];

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(GoogleMapsModule, BrowserModule, MarkdownModule, FormsModule, ReactiveFormsModule, PDFModule, ExcelModule, QPointIconModule, UploadModule, AppRoutingModule, ChartsModule, NgbModule, IntlModule, FloatingLabelModule, InputsModule, SortableModule, NgbAlertModule, MarkdownModule.forRoot(), TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient]
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: TextMissingTranslationHandler,
                deps: [LocalizationNamespaceService]
            }
        }), CommonModule, PDFViewerModule),
        provideAppInitializer(() => {
        const initializerFn = ((injector: Injector) => {
                return () => {
                    const localizationService = injector.get(QPointLocalizationService);
                    const _localizationInfoService = injector.get(LocalizationInfoService);
                    const _localizationNamespacesService = injector.get(LocalizationNamespaceService);
                    const sidebarService = injector.get(QPointSidebarService);
                    sidebarService.updateMenuByRoutes(<any>MENU);
                    localizationService.setTranslation();
                    return Promise.resolve();
                };
            })(inject(Injector));
        return initializerFn();
      }),
        provideAuthentication(Authentication),
        { provide: DI_SETTINGS, useClass: Settings },
        { provide: DI_USER_PROFILE_SERVICE, useClass: QPointProfileService },
        { provide: DI_IMAGE_LOADER_SERVICE, useClass: ImageLoaderService },
        provideLocalizationInfo(LocalizationInfoService),
        provideUserInfoSidebar(UserInfoSidebarProviderService),
        provideNumberFormatService(NumberFormatService),
        provideUserProfileService(QPointProfileService),
        provideImageLoaderService(ImageLoaderService),
        provideQPointI18n(),
        { provide: DI_MAPS_API_KEY_SERVICE, useClass: MapsApiKeyService },
        { provide: DI_FONTAWESOME_ICONS, useValue: FONTAWESOME_ICONS },
        { provide: DI_FONTAWESOME_ICON_PACKS, useValue: [far, fas, fal] },
        {
            provide: DI_TIME_PICKER_FORMAT_PLACEHOLDER,
            useClass: TimePickerConfigService
        },
        {
            provide: DI_UNIT_TRANSLATIONS_BASE_PATH,
            useValue: 'masterData.unit.sign.'
        },
        { provide: DI_LOCALIZATION_INFO, useClass: LocalizationInfoService },
        SidebarSizes,
        QPointContextbarService,
        importProvidersFrom(QPointGridModule),
        { provide: IconSettingsService, useClass: KendoIconSettingsService },
        provideAnimations(),
        provideHttpClient(withJsonpSupport(), withInterceptorsFromDi()),
    ]
})
  .catch(err => console.log(err));
